import { isInputLikeElement } from '@src/utils/dom/inputElement';
import debounce from 'lodash-es/debounce';
import { useEffect, useRef, useState } from 'react';

/**
 * 入力要素のフォーカス状態を追跡するカスタムフック
 *
 * INPUT/TEXTAREA/SELECTなどの入力要素のフォーカス状態を監視し、
 * アクティブ状態を管理します。イベント委譲を使用して動的に追加される要素にも対応。
 *
 * @param {boolean} enabled - 監視を有効にするかどうか（デフォルト: true）
 * @returns {boolean} isInputActive - 入力要素がアクティブかどうか
 */
export const useInputTracking = (enabled = true): boolean => {
  const [isInputActive, setIsInputActive] = useState(false);
  const isInputActiveRef = useRef(isInputActive);

  // 最新のstateをrefに同期
  useEffect(() => {
    isInputActiveRef.current = isInputActive;
  }, [isInputActive]);

  // デバウンス関数をrefで保持
  const debouncedSetInactiveFocus = useRef(debounce(() => setIsInputActive(false), 100)).current;

  const debouncedSetInactiveChange = useRef(debounce(() => setIsInputActive(false), 2000)).current;

  useEffect(() => {
    // 監視が無効の場合は何もしない
    if (!enabled) {
      return;
    }

    const handleInputFocus = (): void => {
      if (!isInputActiveRef.current) {
        setIsInputActive(true);
      }
      debouncedSetInactiveFocus.cancel();
      debouncedSetInactiveChange.cancel();
    };

    const handleInputBlur = (): void => {
      debouncedSetInactiveFocus();
    };

    const handleInputChange = (): void => {
      if (!isInputActiveRef.current) {
        setIsInputActive(true);
      }
      debouncedSetInactiveFocus.cancel();
      debouncedSetInactiveChange();
    };

    // ドロップダウンの開閉イベントリスナー
    const handleDropdownOpened = (): void => {
      handleInputFocus();
    };

    const handleDropdownClosed = (): void => {
      handleInputBlur();
    };

    // カスタムイベントリスナーを追加
    window.addEventListener('dropdown-opened', handleDropdownOpened);
    window.addEventListener('dropdown-closed', handleDropdownClosed);

    // イベント委譲を使用して動的に追加される要素にも対応
    const handleFocusIn = (event: FocusEvent): void => {
      if (isInputLikeElement(event.target)) {
        handleInputFocus();
      }
    };

    const handleFocusOut = (event: FocusEvent): void => {
      if (isInputLikeElement(event.target)) {
        handleInputBlur();
      }
    };

    const handleInput = (event: Event): void => {
      if (isInputLikeElement(event.target)) {
        handleInputChange();
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    document.addEventListener('input', handleInput);

    // DOM変更を監視してMUIメニュー/ポップオーバーの開閉を検知
    // パフォーマンス最適化: 監視範囲を document.body の直接の子要素のみに制限
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        // 追加されたノードのみをチェック（ルートレベルのみ）
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            // MUIのメニュー/ポップオーバーは通常 body の直接の子として追加される
            if (
              element.classList?.contains('MuiMenu-root') ||
              element.classList?.contains('MuiPopover-root')
            ) {
              handleInputFocus();
            }
          }
        }

        // 削除されたノードのみをチェック（ルートレベルのみ）
        for (const node of mutation.removedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            if (
              element.classList?.contains('MuiMenu-root') ||
              element.classList?.contains('MuiPopover-root')
            ) {
              handleInputBlur();
            }
          }
        }
      }
    });

    // subtree: false に変更してパフォーマンスを改善
    observer.observe(document.body, {
      childList: true,
      subtree: false, // body の直接の子要素のみを監視
    });

    return (): void => {
      debouncedSetInactiveFocus.cancel();
      debouncedSetInactiveChange.cancel();
      window.removeEventListener('dropdown-opened', handleDropdownOpened);
      window.removeEventListener('dropdown-closed', handleDropdownClosed);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('input', handleInput);
      observer.disconnect();
    };
  }, [enabled, debouncedSetInactiveFocus, debouncedSetInactiveChange]);

  return isInputActive;
};
